/**
 * 模版库业务层 — 对接 Apifox scj 模板库 Controller（文档 12）
 */

import { templateService } from '@/services/artApi';
import { parseQueryTemplateResult } from '@/services/queryLibraryStore';
import {
  unwrapApiList,
  unwrapApiResult,
  normalizeRemoteCreatedId,
  mapTemplateDtoToPlatform,
  mapTemplateFolderDtoToPlatform,
  mapTemplatePageLayerDto,
  mapPlatformTemplateToImportPayload,
  normalizeInlineTemplateLayerJson,
  normalizeTemplateFolderId,
  normalizeTemplateLayerJson,
} from '@/utils/artMapper';
import {
  buildDepartmentOptions,
  getDepartmentRequestParams,
  getMaterialDepartmentContext,
  getMaterialDepartmentIdParam,
  isAllPlatformStore,
  resolvePlatformStorageKey,
  resolveSyncStoreIds,
  toPlatformStoreId,
  resolveShopId,
} from '@/utils/materialApiContext';
import {
  isPendingTemplate,
  loadPendingTemplates,
  mergeRemoteTemplatesWithPending,
} from '@/services/templatePendingStore';
import { recoverTemplateLayersFromOriginUrl } from '@/services/templatePdfImport';
import {
  pollTemplateLayerJson,
  importPdfTemplateLayersFromOss,
} from '@/services/pdfParseTaskStore';

function storeIdFromContext(storeId, user, stores = []) {
  return resolvePlatformStorageKey(storeId, user, stores);
}

function departmentParams(storeId, user, apiContext = {}) {
  const { stores, sessionDept } = buildDepartmentOptions({ storeId, user, ...apiContext });
  return getDepartmentRequestParams(storeId, user, stores, sessionDept);
}

function departmentContextForWrite(storeId, user, apiContext = {}) {
  const { stores, sessionDept } = buildDepartmentOptions({ storeId, user, ...apiContext });
  return getMaterialDepartmentContext(storeId, user, stores, sessionDept);
}

function departmentGetParams(storeId, user, apiContext = {}) {
  const { stores, sessionDept } = buildDepartmentOptions({ storeId, user, ...apiContext });
  return getMaterialDepartmentIdParam(storeId, user, stores, sessionDept);
}

export async function fetchTemplateFolders(platform, storeId, user, parentId, apiContext = {}) {
  const { stores } = buildDepartmentOptions({ storeId, user, ...apiContext });
  const sid = storeIdFromContext(storeId, user, stores);
  const deptParams = departmentGetParams(storeId, user, apiContext);
  let payload;
  try {
    payload = await templateService.listFolders({
      ...deptParams,
      parentId: parentId != null ? Number(parentId) : 0,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[art-api] 模版文件夹同步失败，保留本地缓存', error);
    }
    return platform.templateFolders?.[sid] || [];
  }
  if (!platform.templateFolders) platform.templateFolders = {};
  platform.templateFolders[sid] = unwrapApiList(payload).map(mapTemplateFolderDtoToPlatform);
  return platform.templateFolders[sid];
}

export async function fetchTemplates(platform, storeId, user, query = {}, apiContext = {}) {
  const { stores } = buildDepartmentOptions({ storeId, user, ...apiContext });
  const sid = storeIdFromContext(storeId, user, stores);
  const deptParams = departmentGetParams(storeId, user, apiContext);
  let payload;
  try {
    payload = await templateService.queryTemplate(deptParams);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[art-api] 模版列表同步失败，保留本地/待同步记录', error);
    }
    const pendingFromStorage = loadPendingTemplates(storeId === 'all' ? undefined : sid);
    const pendingFromMemory = (platform.templates || []).filter(
      item => item.storeId === sid && (isPendingTemplate(item) || item.pendingPersistence),
    );
    return {
      list: platform.templates?.filter(item => item.storeId === sid) || [],
      total: platform.templates?.filter(item => item.storeId === sid).length || 0,
      syncFailed: true,
      pending: [...pendingFromStorage, ...pendingFromMemory],
    };
  }
  const { folders, templates: remoteList } = parseQueryTemplateResult(payload, sid);
  if (!platform.templateFolders) platform.templateFolders = {};
  platform.templateFolders[sid] = folders;
  const remote = remoteList.filter(template => template.id);
  const pendingFromStorage = loadPendingTemplates(storeId === 'all' ? undefined : sid);
  const pendingFromMemory = (platform.templates || []).filter(isPendingTemplate);
  platform.templates = mergeRemoteTemplatesWithPending(
    remote,
    [...pendingFromStorage, ...pendingFromMemory],
  );
  const remoteIds = new Set(remote.map(item => String(item.id)));
  return {
    list: platform.templates.filter(item => remoteIds.has(String(item.id))),
    total: remote.length,
  };
}

export async function syncTemplateLibrary(platform, storeId, user, apiContext = {}) {
  const context = { ...apiContext, storeId, user };
  const storeIds = resolveSyncStoreIds(context);
  await Promise.all(storeIds.map((sid) =>
    fetchTemplates(platform, sid, user, {}, context),
  ));
}

export async function createTemplateFolderRemote(storeId, user, folderName, parentId, stores = [], apiContext = {}) {
  const payload = await templateService.createFolder({
    ...departmentParams(storeId, user, { stores, ...apiContext }),
    folderName,
    parentId: parentId != null ? Number(parentId) : 0,
  });
  return normalizeRemoteCreatedId(payload.result ?? unwrapApiResult(payload));
}

export async function renameTemplateFolderRemote(folderId, folderName) {
  await templateService.renameFolder({
    id: Number(folderId) || folderId,
    folderName,
  });
}

export async function deleteTemplateFolderRemote(folderId) {
  await templateService.deleteFolder({ id: Number(folderId) || folderId });
}

/** 拖拽模板归档/移出：调用 /api/art/template/batch-move */
export async function moveTemplateToFolderRemote(templateId, folderId) {
  const targetFolderId = folderId != null && folderId !== '' ? (Number(folderId) || folderId) : 0;
  const id = Number(templateId) || templateId;
  await batchMoveTemplatesRemote(targetFolderId, [id]);
}

export async function batchMoveTemplatesRemote(targetFolderId, ids = []) {
  const normalizedTargetFolderId = targetFolderId != null && targetFolderId !== ''
    ? (Number(targetFolderId) || targetFolderId)
    : 0;
  const numIds = (Array.isArray(ids) ? ids : [ids]).map(id => Number(id) || id);
  await templateService.batchMove(normalizedTargetFolderId, numIds);
}

async function resolveTemplateLayerJsonSource(tplMeta = {}) {
  return normalizeTemplateLayerJson(tplMeta.layerJson);
}

function isHttpCoverThumbUrl(value) {
  return /^https?:\/\//i.test(String(value ?? '').trim());
}

/** PDF 导入 /api/art/template/import 前必须备齐 trackId 与封面 OSS 地址 */
export function assemblePdfTemplateImportFields({ trackId, templateLayerId, coverThumbUrl } = {}) {
  const layerId = String(templateLayerId ?? trackId ?? '').trim();
  const cover = String(coverThumbUrl ?? '').trim();
  if (!layerId) {
    throw new Error('未获取到 templateLayerId，无法导入模版');
  }
  if (!isHttpCoverThumbUrl(cover)) {
    throw new Error('未获取到有效封面 coverThumbUrl，无法导入模版');
  }
  return {
    templateLayerId: layerId,
    coverThumbUrl: cover,
  };
}

export async function importTemplateRemote(storeId, user, tplMeta = {}, stores = [], apiContext = {}) {
  const dept = departmentContextForWrite(storeId, user, { stores, ...apiContext });
  const requestDept = departmentParams(storeId, user, { stores, ...apiContext });
  const layerJson = await resolveTemplateLayerJsonSource(tplMeta);
  const body = mapPlatformTemplateToImportPayload({
    ...tplMeta,
    departmentId: tplMeta.departmentId ?? dept.departmentId,
    departmentName: tplMeta.departmentName ?? dept.departmentName,
    layerJson,
  });
  const payload = await templateService.import(body, requestDept);
  const sid = storeIdFromContext(storeId, user, stores);
  return mapTemplateDtoToPlatform({
    id: payload.result,
    folderId: body.folderId,
    templateName: body.templateName,
    templateType: body.templateType,
    coverThumbUrl: body.coverThumbUrl || tplMeta.thumb || tplMeta.coverThumbUrl || '',
    originFileUrl: body.originFileUrl,
    layerJson: body.layerJson,
    departmentId: body.departmentId,
    departmentName: body.departmentName,
    templateLayerId: body.templateLayerId || tplMeta.templateLayerId || tplMeta.trackId || '',
  }, sid);
}

/**
 * 文档 18：POST /api/art/template/import — 导入模版并将分层 JSON 写入 layerJson 字段
 */
export async function importTemplateWithLayerStorageRemote(
  storeId,
  user,
  tplMeta = {},
  stores = [],
  { requireLayerJson = true, autoUseTemplate = false, ...apiContext } = {},
) {
  const layerJson = await resolveTemplateLayerJsonSource(tplMeta);
  if (requireLayerJson && !hasUsableLayerJson(layerJson)) {
    throw new Error('缺少模版分层数据 layerJson，无法保存到云端');
  }
  const template = await importTemplateRemote(storeId, user, {
    ...tplMeta,
    layerJson,
  }, stores, apiContext);

  // /api/art/template/use 会生成店铺「_副本」，导入母模版时默认不要调用。
  let usedResult = null;
  if (autoUseTemplate === true && template?.id) {
    try {
      usedResult = await useTemplateRemote(storeId, user, template.id, tplMeta.folderId, stores, apiContext);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[art-api] 模版导入完成后调用 /api/art/template/use 失败', error);
      }
    }
  }

  const finalTemplate = {
    ...template,
    ...(usedResult || {}),
    layerJson: usedResult?.layerJson || layerJson || template.layerJson,
    pendingLayerSync: false,
  };

  return {
    template: finalTemplate,
    layerJson: finalTemplate.layerJson,
    layerStorageConfirmed: true,
  };
}

export function isRemoteTemplateId(templateId) {
  const id = String(templateId || '').trim();
  return Boolean(id) && !id.startsWith('t_');
}

export async function updateTemplateContentRemote(templateId, tplMeta = {}) {
  const layerJson = await resolveTemplateLayerJsonSource(tplMeta);
  if (!hasUsableLayerJson(layerJson)) {
    throw new Error('缺少模版分层数据 layerJson，无法更新母模版');
  }
  const coverThumbUrl = tplMeta.coverThumbUrl || (
    tplMeta.thumb && /^https?:\/\//i.test(String(tplMeta.thumb).trim())
      ? String(tplMeta.thumb).trim()
      : undefined
  );
  const body = {
    id: Number(templateId) || templateId,
    layerJson,
    templateName: tplMeta.name || tplMeta.templateName || undefined,
  };
  if (coverThumbUrl) body.coverThumbUrl = coverThumbUrl;
  if (tplMeta.originFileUrl) body.originFileUrl = tplMeta.originFileUrl;
  if (tplMeta.folderId != null) {
    const folderId = normalizeTemplateFolderId(tplMeta.folderId);
    body.folderId = folderId ? Number(folderId) : 0;
  }
  await templateService.updateAll(body);
  return {
    ...tplMeta,
    id: String(templateId),
    layerJson,
    thumb: coverThumbUrl || tplMeta.thumb,
    pendingMasterEdit: false,
    masterConfirmed: true,
    pendingPersistence: false,
  };
}

export async function renameTemplateRemote(templateId, templateName, coverThumbUrl) {
  await templateService.update({
    id: Number(templateId) || templateId,
    templateName,
    coverThumbUrl,
  });
}

export async function deleteTemplateRemote(templateId) {
  await templateService.delete({ id: Number(templateId) || templateId });
}

export async function useTemplateRemote(storeId, user, templateId, folderId, stores = [], apiContext = {}) {
  const requestDept = departmentParams(storeId, user, { stores, ...apiContext });
  const payload = await templateService.use({
    ...requestDept,
    templateId: Number(templateId) || templateId,
    folderId: normalizeTemplateFolderId(folderId) ? Number(normalizeTemplateFolderId(folderId)) : undefined,
  });
  const sid = storeIdFromContext(storeId, user, stores);
  const dto = unwrapApiResult(payload) || {};
  const mapped = mapTemplateDtoToPlatform(dto, sid);
  return {
    ...mapped,
    // 保留请求的 templateId，避免后端返回的自增使用记录 ID (如 48) 覆盖了模版本身 ID (如 47)
    id: String(templateId || mapped.id),
    templateId: String(templateId || mapped.id),
    useRecordId: dto.id != null ? String(dto.id) : undefined,
  };
}

export async function fetchTemplateDetail(templateId) {
  const payload = await templateService.getDetail(templateId);
  return unwrapApiResult(payload);
}

export async function fetchTemplatePageLayers(templateId) {
  const payload = await templateService.listPageLayers(templateId);
  return unwrapApiList(payload).map(mapTemplatePageLayerDto);
}

function hasUsableLayerJson(layerJson) {
  try {
    const normalized = normalizeInlineTemplateLayerJson(layerJson);
    const parsed = JSON.parse(normalized);
    return Boolean(parsed && typeof parsed === 'object');
  } catch {
    return false;
  }
}

export { hasUsableLayerJson as hasUsableTemplateLayerJson };

export function mergeTemplatePageLayers(layers = []) {
  const valid = (layers || []).filter(layer => hasUsableLayerJson(layer?.layerJson));
  if (!valid.length) return null;
  if (valid.length === 1) return valid[0].layerJson;
  const pages = valid.flatMap((layer, layerIndex) => {
    const parsed = JSON.parse(layer.layerJson);
    const sourcePages = Array.isArray(parsed.pages) && parsed.pages.length
      ? parsed.pages
      : [parsed];
    return sourcePages.map((page, pageIndex) => ({
      ...page,
      pageIndex: layer.pageIndex ?? page.pageIndex ?? (layerIndex + pageIndex),
      name: layer.pageName || page.name || `设计方案 ${layerIndex + pageIndex + 1}`,
      thumb: layer.thumb || page.thumb || '',
      syncRelationJson: layer.syncRelationJson || page.syncRelationJson || '',
      bagAreaJson: layer.bagAreaJson || page.bagAreaJson || '',
    }));
  });
  return JSON.stringify({
    mode: 'layers',
    pages,
  });
}

function resolveCachedTemplateLayers(templateId, existing = {}) {
  if (hasUsableLayerJson(existing.layerJson)) {
    return {
      layerJson: existing.layerJson,
      importStats: existing.importStats,
      recoveredFromSource: existing.recoveredFromSource,
      pendingPersistence: existing.pendingPersistence,
    };
  }
  if (typeof localStorage !== 'undefined') {
    try {
      const directKey = `jx_editor_layer_json_${templateId}`;
      const cached = localStorage.getItem(directKey)
        || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(directKey));
      if (hasUsableLayerJson(cached)) {
        return {
          layerJson: cached,
          recoveredFromSource: false,
          pendingPersistence: false,
        };
      }
    } catch { /* ignore */ }
  }
  const pending = loadPendingTemplates().find(item => String(item.id) === String(templateId));
  if (!pending || !hasUsableLayerJson(pending.layerJson)) return null;
  return {
    layerJson: pending.layerJson,
    importStats: pending.importStats,
    recoveredFromSource: pending.recoveredFromSource,
    pendingPersistence: pending.pendingPersistence,
  };
}

export function mergeRecoveredTemplatePersistence(working = {}, persisted = null) {
  if (!persisted) return working;
  return {
    ...working,
    ...persisted,
    // 本地空间不足时，持久化层会返回去掉大字段的精简记录。
    // 精简记录只用于缓存元数据，不能覆盖本次已恢复、可直接渲染的分层内容。
    layerJson: hasUsableLayerJson(persisted.layerJson)
      ? persisted.layerJson
      : working.layerJson,
  };
}

/**
 * 远程模版打开：详情 / 分页图层 → 本机缓存 → 原 PDF 自动恢复。
 * 默认不调用 /api/art/template/use：该接口会在模版库落一条「_副本」。
 * 仅当业务明确要记使用次数（recordUse: true）时才打 /use。
 */
export async function resolveRemoteTemplateForUse({
  storeId,
  user,
  templateId,
  existing = {},
  stores = [],
  departmentId,
  departmentName,
  recoverFromPdf = true,
  recordUse = false,
  onRecoverStart,
  onRecoverSuccess,
  persistRecovered,
} = {}) {
  const apiContext = { stores, departmentId, departmentName };
  let working;
  let useError = null;

  if (recordUse === true && templateId) {
    try {
      const rawWorking = await useTemplateRemote(
        storeId,
        user,
        templateId,
        existing.folderId,
        stores,
        apiContext,
      );
      working = {
        ...existing,
        ...rawWorking,
        id: String(templateId || rawWorking.id || existing.id),
        name: (rawWorking.name && rawWorking.name !== '未命名模版') ? rawWorking.name : (existing.name || rawWorking.name || '未命名模版'),
        thumb: rawWorking.thumb || existing.thumb || '',
        originFileUrl: rawWorking.originFileUrl || existing.originFileUrl || '',
        folderId: rawWorking.folderId ?? existing.folderId,
        storeId: rawWorking.storeId || existing.storeId || storeId,
        layerJson: rawWorking.layerJson || existing.layerJson || '',
        templateLayerId: rawWorking.templateLayerId || existing.templateLayerId || '',
      };
    } catch (error) {
      useError = error;
    }
  }

  if (!working) {
    let fallback = { ...existing };
    let cachedLayerJson = await resolveTemplateLayerJsonSource(existing).catch(() => '');
    if (!hasUsableLayerJson(cachedLayerJson)) {
      try {
        const detail = await fetchTemplateDetail(templateId);
        const mapped = mapTemplateDtoToPlatform(detail, existing.storeId || storeId || 's1');
        fallback = {
          ...fallback,
          ...mapped,
          id: String(templateId || mapped.id || existing.id),
          name: (mapped.name && mapped.name !== '未命名模版')
            ? mapped.name
            : (existing.name || mapped.name || '未命名模版'),
          thumb: mapped.thumb || existing.thumb || '',
          originFileUrl: mapped.originFileUrl || existing.originFileUrl || '',
          templateLayerId: mapped.templateLayerId || existing.templateLayerId || '',
        };
        cachedLayerJson = await resolveTemplateLayerJsonSource(fallback).catch(() => '');
      } catch {
        /* 详情不可用时继续尝试 page-layers / 原 PDF 恢复 */
      }
    }
    if (!hasUsableLayerJson(cachedLayerJson) && !fallback.originFileUrl && useError) {
      throw useError;
    }
    working = {
      ...fallback,
      id: String(fallback.id || templateId),
      layerJson: cachedLayerJson,
      useSyncFailed: Boolean(useError),
    };
  }
  working.originFileUrl = working.originFileUrl || existing.originFileUrl;
  working.templateLayerId = working.templateLayerId || existing.templateLayerId || '';

  const normalizedLayerJson = await resolveTemplateLayerJsonSource(working).catch(() => '');
  if (hasUsableLayerJson(normalizedLayerJson)) working.layerJson = normalizedLayerJson;

  const cached = resolveCachedTemplateLayers(working.id, {
    ...existing,
    layerJson: hasUsableLayerJson(working.layerJson) ? working.layerJson : existing.layerJson,
  });
  if (cached) {
    working = { ...working, ...cached };
  }

  // use 接口可能只返回一次“使用记录”，即使请求成功也不一定携带模板分层。
  // 此时必须继续读取模板详情，不能直接把空 use DTO 当作模板本体。
  if (!hasUsableLayerJson(working.layerJson)) {
    try {
      const detailDto = await fetchTemplateDetail(templateId);
      const detail = mapTemplateDtoToPlatform(detailDto, working.storeId || existing.storeId || storeId || 's1');
      const detailLayerJson = await resolveTemplateLayerJsonSource(detail).catch(() => '');
      working = {
        ...working,
        ...detail,
        id: String(templateId || working.id || detail.id),
        name: (detail.name && detail.name !== '未命名模版') ? detail.name : working.name,
        thumb: detail.thumb || working.thumb,
        originFileUrl: detail.originFileUrl || working.originFileUrl,
        templateLayerId: detail.templateLayerId || working.templateLayerId || '',
        folderId: detail.folderId ?? working.folderId,
        storeId: detail.storeId || working.storeId || storeId,
        layerJson: hasUsableLayerJson(detailLayerJson) ? detailLayerJson : working.layerJson,
      };
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[art-api] 模板详情未返回可用分层，继续尝试解析任务 / 分页图层 / 原 PDF 恢复', error);
      }
    }
  }

  // 若存在 templateLayerId / trackId，优先直接向服务端查询已解析的分层数据
  if (!hasUsableLayerJson(working.layerJson) && working.templateLayerId) {
    try {
      const parseResult = await pollTemplateLayerJson(working.templateLayerId, {
        maxAttempts: 4,
        intervalMs: 800,
      });
      if (hasUsableLayerJson(parseResult?.layerJson)) {
        working.layerJson = parseResult.layerJson;
        working.pageCount = parseResult.pageTotal || working.pageCount;
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[art-api] templateLayerId 分层查询失败，继续尝试其他恢复路径', error);
      }
    }
  }

  if (!hasUsableLayerJson(working.layerJson)) {
    try {
      const layers = await fetchTemplatePageLayers(working.id);
      working.layerJson = mergeTemplatePageLayers(layers);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[art-api] 模板分页图层读取失败，继续尝试原 PDF 恢复', error);
      }
    }
  }

  if (!hasUsableLayerJson(working.layerJson) && recoverFromPdf && working.originFileUrl) {
    onRecoverStart?.();
    let recovered = null;
    if (/^https?:\/\//i.test(String(working.originFileUrl).trim())) {
      try {
        const cloudTask = await importPdfTemplateLayersFromOss({
          ossUrl: working.originFileUrl,
          createUserId: user?.id,
        });
        if (hasUsableLayerJson(cloudTask?.layerJson)) {
          recovered = {
            layerJson: cloudTask.layerJson,
            pageCount: cloudTask.pageTotal,
          };
        }
      } catch (cloudErr) {
        if (import.meta.env.DEV) {
          console.warn('[art-api] 云端任务恢复原 PDF 分层失败，尝试本地引擎', cloudErr);
        }
      }
    }

    if (!hasUsableLayerJson(recovered?.layerJson)) {
      try {
        recovered = await recoverTemplateLayersFromOriginUrl(working.originFileUrl, {
          name: working.name,
          storeId: working.storeId || storeId,
          folderId: working.folderId,
          dpi: 180,
        });
      } catch (localErr) {
        if (import.meta.env.DEV) {
          console.warn('[art-api] 本地引擎恢复原 PDF 分层失败', localErr);
        }
      }
    }

    if (hasUsableLayerJson(recovered?.layerJson)) {
      working = {
        ...working,
        thumb: recovered.thumb || working.thumb,
        layerJson: recovered.layerJson,
        pageCount: recovered.pageCount || working.pageCount,
        importStats: recovered.importStats,
        recoveredFromSource: true,
        pendingPersistence: true,
      };
      working = mergeRecoveredTemplatePersistence(
        working,
        persistRecovered?.(working),
      );
      onRecoverSuccess?.();
    }
  }

  if (hasUsableLayerJson(working.layerJson)) {
    try {
      if (typeof localStorage !== 'undefined') {
        const ids = [working.id, templateId].filter(Boolean);
        ids.forEach((id) => {
          localStorage.setItem(`jx_editor_layer_json_${id}`, working.layerJson);
        });
      }
    } catch { /* ignore quota */ }
  }

  if (!hasUsableLayerJson(working.layerJson)) {
    throw new Error('该模版没有可用的分层数据，请重新导入 PDF');
  }

  return working;
}
